import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Plus,
  X,
  CheckCircle,
  XCircle,
  ChatCircle,
  Clock,
  ArrowClockwise,
  Gavel,
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { OfferBriefModal } from './OfferBriefModal';
import { DirectApprovalModal } from './DirectApprovalModal';
import { ReturnInstructionsModal } from './ReturnInstructionsModal';
import {
  listForCase,
  withdrawAllocation,
  completeAllocation,
  directApproveAllocation,
  directDeclineAllocation,
  type Allocation,
  STATUS_LABEL,
  DECLINE_REASON_LABEL,
  describeConference,
} from '@/lib/allocationsApi';
import { getApiErrorMessage } from '@/lib/api';
import { onAllocationUpdate, useWebSocketStore } from '@/lib/websocket';
import { useAuthStore } from '@/features/auth/store';
import { useMattersStore } from '@/lib/mattersStore';
import { useClerkWorkspaceStore } from '@/lib/clerkWorkspaceStore';
import { toast } from 'sonner';

interface Props {
  caseId: string;
  caseLabel: string;
}

const BARRISTER_TYPES = ['silk', 'junior', 'pupil', 'door_tenant'];

export function AllocationStatusCard({ caseId, caseLabel }: Props) {
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOffer, setShowOffer] = useState(false);
  const [directModal, setDirectModal] = useState<'approve' | 'decline' | null>(null);
  const [returnModal, setReturnModal] = useState<{ allocation: Allocation; mode: 'request' | 'record' } | null>(null);

  // Handoff from the Conflict Review page. A decision of cease/reassign does NOT remove
  // counsel — it returns the allocation that must be returned, and the clerk completes it
  // through this lifecycle. Landing here without opening the right allocation would be a
  // dead end, so the parameter is consumed rather than decorative.
  const [searchParams, setSearchParams] = useSearchParams();
  const handoffAllocationId = searchParams.get('returnAllocation');

  const currentUser = useAuthStore((s) => s.user);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  const hasAllocationsPerm = useClerkWorkspaceStore((s) => s.hasPermission('RESPOND_TO_ALLOCATIONS'));
  const wsConnected = useWebSocketStore((s) => s.connected);
  const queryClient = useQueryClient();
  const [completing, setCompleting] = useState(false);

  const reload = () => {
    setLoading(true);
    listForCase(caseId)
      .then(setAllocations)
      .catch((err) => setError(getApiErrorMessage(err, 'Could not load allocation history')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!handoffAllocationId || allocations.length === 0) return;
    const target = allocations.find((a) => a.id === handoffAllocationId);
    if (!target) return;
    // `accepted` -> the return still has to be requested (ground + explanation).
    // `return_requested` -> the request exists; the clerk records it.
    if (target.status === 'accepted' || target.status === 'return_requested') {
      setReturnModal({ allocation: target, mode: target.status === 'accepted' ? 'request' : 'record' });
    }
    // Consume the parameter so a refresh doesn't reopen the modal.
    const next = new URLSearchParams(searchParams);
    next.delete('returnAllocation');
    setSearchParams(next, { replace: true });
  }, [handoffAllocationId, allocations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Polling + WebSocket subscription
  useEffect(() => {
    const interval = setInterval(reload, 300_000);
    const unsubWs = onAllocationUpdate(() => reload());
    return () => {
      clearInterval(interval);
      unsubWs();
    };
  }, [caseId, wsConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = allocations.find(
    (a) => a.status === 'pending' || a.status === 'conference_requested',
  );
  const accepted = allocations.find((a) => a.status === 'accepted');
  const pendingReturn = allocations.find((a) => a.status === 'return_requested');
  const completed = allocations.filter((a) => a.status === 'completed');
  const history = allocations.filter(
    (a) => a.status === 'declined' || a.status === 'withdrawn' || a.status === 'completed',
  );
  
  const isAdminUser = currentUser?.memberType === 'admin' || currentUser?.memberType === 'staff';
  const isClerkUser = currentUser?.memberType === 'senior_clerk' || currentUser?.memberType === 'junior_clerk';
  const canOfferBrief = isAdminUser || (isClerkUser && hasAllocationsPerm);
  const canComplete = !!accepted && (isClerkUser || accepted.barrister.user.id === currentUser?.id);

  // Shown only to the barrister this brief was offered to, when they are
  // waiting on a conference that hasn't happened yet.
  const isBarrister = BARRISTER_TYPES.includes(currentUser?.memberType ?? '');
  const isTargetBarrister =
    isBarrister &&
    open != null &&
    open.barrister.user.id === currentUser?.id;

  const handleDirectAction = async (note: string) => {
    if (!open) return;
    if (directModal === 'approve') {
      await directApproveAllocation(open.id, note);
      toast.success('Matter approved. It is now active in your caseload.');
    } else {
      await directDeclineAllocation(open.id, note);
      toast.success('Matter declined. The clerk has been notified.');
    }
    setDirectModal(null);
    reload();
    fetchMatters();
  };

  // POST /allocations/:id/complete (accepted → completed) existed with no caller — accepted work
  // could never be closed. Success is announced only after the server accepts it.
  const handleComplete = async () => {
    if (!accepted || completing) return;
    const who = `${accepted.barrister.user.firstName} ${accepted.barrister.user.lastName}`;
    if (!confirm(`Mark this brief as completed by ${who}? This closes the allocation and cannot be undone.`)) return;
    setCompleting(true);
    try {
      await completeAllocation(accepted.id);
      toast.success('Allocation marked complete');
      reload();
      fetchMatters();
      void queryClient.invalidateQueries({ queryKey: ['allocations'] });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not complete the allocation'));
    } finally {
      setCompleting(false);
    }
  };

  const handleWithdraw = async () => {
    if (!open) return;
    if (!confirm('Withdraw this offer? You can re-allocate to another barrister afterwards.')) return;
    try {
      await withdrawAllocation(open.id);
      toast.success('Offer withdrawn');
      reload();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not withdraw offer'));
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
          <CardTitle className="flex items-center gap-2 text-[15px]">
            <Briefcase className="h-4 w-4 text-brand-600 dark:text-brand-400" weight="duotone" />
            Allocation
          </CardTitle>
          {!open && !accepted && canOfferBrief && (
            <Button
              size="sm"
              variant="primary"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => setShowOffer(true)}
            >
              Offer brief
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="text-xs text-muted-foreground">Loading...</div>
          ) : error ? (
            <div className="rounded-md border border-danger/30 bg-danger-bg px-3 py-2 text-xs text-danger">
              {error}
            </div>
          ) : (
            <>
              {/* Currently open offer */}
              {open && (
                <div className="rounded-md border border-brand-200 bg-brand-50/40 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-brand-700 dark:text-brand-300">
                        <Clock className="h-3 w-3" />
                        {STATUS_LABEL[open.status]}
                      </div>
                      <div className="mt-1 text-sm font-medium text-foreground">
                        {open.barrister.user.firstName} {open.barrister.user.lastName}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Offered {timeAgo(open.offeredAt)} by{' '}
                        {open.offeredBy.firstName} {open.offeredBy.lastName}
                      </div>
                      {open.status === 'conference_requested' && open.requestedConference && (
                        <div className="mt-2">
                          {open.requestedConference.scheduledAt ? (
                            // Conference has been scheduled by the clerk — show date/time prominently
                            <div className="rounded-md bg-amber-50 dark:bg-amber-500/10 px-2.5 py-2 ring-1 ring-amber-200 dark:ring-amber-500/30">
                              <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-amber-700 dark:text-amber-300">
                                <ChatCircle className="h-3 w-3" weight="fill" />
                                Conference confirmed by clerk
                              </div>
                              <div className="mt-1 text-xs font-semibold text-amber-900 dark:text-amber-200">
                                {new Date(open.requestedConference.scheduledAt).toLocaleString('en-GB', {
                                  weekday: 'short', day: 'numeric', month: 'short',
                                  hour: '2-digit', minute: '2-digit',
                                })}
                                {' '}&middot;{' '}{open.requestedConference.durationMinutes} min
                              </div>
                              <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
                                {describeConference(open.requestedConference)}
                              </div>
                            </div>
                          ) : (
                            // Conference requested but not yet scheduled by clerk
                            <div className="inline-flex items-center gap-1.5 rounded-md bg-card px-2 py-1 text-xs text-foreground ring-1 ring-brand-200">
                              <ChatCircle className="h-3 w-3 text-brand-600 dark:text-brand-400" weight="fill" />
                              Conference requested:{' '}{describeConference(open.requestedConference)}
                            </div>
                          )}
                        </div>
                      )}
                      {open.clerkNote && (
                        <div className="mt-1.5 text-xs italic text-muted-foreground">
                          {'"'}{open.clerkNote}{'"'}
                        </div>
                      )}

                      {/* Direct approve / decline — shown only to the target barrister.
                          The barrister must explicitly accept or decline — scheduling a
                          conference does NOT auto-accept. They can act at any point. */}
                      {isTargetBarrister && open.status === 'conference_requested' && (
                        <div className="mt-3 space-y-2 rounded-xl border border-brand-200 bg-brand-50/30 p-3">
                          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-brand-700 dark:text-brand-300">
                            <Gavel className="h-3 w-3" weight="duotone" />
                            {open.requestedConference?.scheduledAt
                              ? 'After the conference — or if you already know:'
                              : 'Already have enough information?'}
                          </div>
                          <p className="text-xs leading-relaxed text-muted-foreground">
                            {open.requestedConference?.scheduledAt
                              ? 'Approve or decline this matter. If you spoke with the clerk directly, you can decide now without waiting.'
                              : 'You can approve or decline this matter directly, without waiting for the conference.'}
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setDirectModal('approve')}
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-800 dark:text-emerald-300 transition hover:bg-emerald-100 dark:hover:bg-emerald-500/20 dark:bg-emerald-500/15"
                            >
                              <CheckCircle className="h-3.5 w-3.5" weight="bold" />
                              Approve Matter
                            </button>
                            <button
                              onClick={() => setDirectModal('decline')}
                              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-700 dark:text-red-300 transition hover:bg-red-100 dark:hover:bg-red-500/20 dark:bg-red-500/15"
                            >
                              <XCircle className="h-3.5 w-3.5" weight="bold" />
                              Decline Matter
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {!isTargetBarrister && (
                      <button
                        onClick={handleWithdraw}
                        className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 text-xs font-medium text-muted-foreground transition hover:border-danger/40 hover:bg-danger-bg hover:text-danger"
                      >
                        <X className="h-3 w-3" />
                        Withdraw
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Accepted */}
              {accepted && (
                <div className="rounded-md border border-success/30 bg-success-bg/50 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-success">
                    <CheckCircle className="h-3 w-3" weight="fill" />
                    Accepted
                  </div>
                  <div className="mt-1 text-sm font-medium text-foreground">
                    {accepted.barrister.user.firstName} {accepted.barrister.user.lastName}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Accepted{' '}
                    {accepted.respondedAt ? timeAgo(accepted.respondedAt) : 'recently'}
                  </div>
                  {accepted.barristerNote && (
                    <div className="mt-1.5 text-xs italic text-muted-foreground">
                      {'"'}{accepted.barristerNote}{'"'}
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {canComplete && (
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<CheckCircle className="h-3.5 w-3.5" />}
                        disabled={completing}
                        onClick={handleComplete}
                      >
                        {completing ? 'Completing…' : 'Mark complete'}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setReturnModal({ allocation: accepted, mode: 'request' })}
                    >
                      Return instructions
                    </Button>
                  </div>
                </div>
              )}

              {/* Counsel has asked to return but is STILL ON RECORD. The clerk closes it. */}
              {pendingReturn && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-amber-800 dark:text-amber-300">
                    <ArrowClockwise className="h-3 w-3" weight="bold" />
                    Return requested
                  </div>
                  <div className="mt-1 text-sm font-medium text-foreground">
                    {pendingReturn.barrister.user.firstName} {pendingReturn.barrister.user.lastName}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Still instructed. The return must be recorded before they come off the record.
                  </p>
                  {pendingReturn.returnNarrative && (
                    <div className="mt-1.5 text-xs italic text-muted-foreground">
                      {'"'}{pendingReturn.returnNarrative}{'"'}
                    </div>
                  )}
                  {!isBarrister && (
                    <Button
                      size="sm"
                      className="mt-2"
                      onClick={() => setReturnModal({ allocation: pendingReturn, mode: 'record' })}
                    >
                      Record the return
                    </Button>
                  )}
                </div>
              )}

              {/* No open + no accepted */}
              {/* Not after a completed brief — "no barrister has been offered" would be false. */}
              {!open && !accepted && completed.length === 0 && (
                <div className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-4 text-center">
                  <p className="text-xs text-muted-foreground">
                    No barrister has been offered this brief yet.
                  </p>
                </div>
              )}

              {/* History (declined, withdrawn) */}
              {history.length > 0 && (
                <div>
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    Previous offers
                  </div>
                  <ul className="space-y-1.5">
                    {history.map((a) => (
                      <li key={a.id} className="flex items-start gap-2 text-xs">
                        {a.status === 'completed' ? (
                          <CheckCircle className="mt-0.5 h-3 w-3 flex-shrink-0 text-success" weight="fill" />
                        ) : a.status === 'declined' ? (
                          <XCircle className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground" weight="fill" />
                        ) : (
                          <ArrowClockwise className="mt-0.5 h-3 w-3 flex-shrink-0 text-muted-foreground" weight="fill" />
                        )}
                        <div className="min-w-0">
                          <div className="text-foreground">
                            {a.barrister.user.firstName} {a.barrister.user.lastName}{' '}
                            <span className="text-muted-foreground">{'— '}{STATUS_LABEL[a.status]}</span>
                          </div>
                          {a.declineReason && (
                            <div className="text-muted-foreground">
                              Reason: {DECLINE_REASON_LABEL[a.declineReason]}
                              {a.declineReasonOther ? ` — ${a.declineReasonOther}` : ''}
                            </div>
                          )}
                          {a.barristerNote && (
                            <div className="italic text-muted-foreground">
                              {'"'}{a.barristerNote}{'"'}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <OfferBriefModal
        caseId={caseId}
        caseLabel={caseLabel}
        open={showOffer}
        onClose={() => setShowOffer(false)}
        onSuccess={reload}
      />

      {directModal && open && (
        <DirectApprovalModal
          mode={directModal}
          matterName={caseLabel}
          onConfirm={handleDirectAction}
          onClose={() => setDirectModal(null)}
        />
      )}

      {returnModal && (
        <ReturnInstructionsModal
          allocation={returnModal.allocation}
          mode={returnModal.mode}
          open
          onClose={() => setReturnModal(null)}
          onSuccess={() => { reload(); fetchMatters(); }}
        />
      )}
    </>
  );
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}
